import HotelPlaceholderImage from 'assets/hotel-placeholder.svg';
import React from 'react';

import Image from 'components/Image';
import {
  CreateAmadeusBookingMutation,
  HotelBookingPricing,
} from 'generated/graphql';
import { calculateTotalGuests } from 'utilities/guestQuantityHelpers';
import { formatCents, getPricingInfo, PLUS_TAX_TEXT } from 'utilities/money';

// Pick only the fields that OfferDetailsCard actually uses
type BookingDisplayData = Pick<
  CreateAmadeusBookingMutation['createAmadeusBooking'],
  | 'avgPrice'
  | 'basePrice'
  | 'estimatedTax'
  | 'grandTotalAfterTax'
  | 'guestsPerRoom'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'nights'
  | 'roomBedType'
  | 'cancellationDetails'
  | 'hotel'
> & {
  // Make thsPrice optional since it might not be available in all contexts
  thsPrice?: CreateAmadeusBookingMutation['createAmadeusBooking']['thsPrice'];
};

interface Props {
  booking: BookingDisplayData;
  preview?: HotelBookingPricing;
  showRatesDetails?: boolean;
  hideRoomGuestCounts?: boolean;
}

const OfferDetailsCard: React.FC<Props> = ({
  booking,
  preview,
  showRatesDetails = true,
  hideRoomGuestCounts = false,
}) => {
  // Helper function to safely get image URL
  const getImageUrl = () => {
    if (booking?.hotel?.__typename === 'AmadeusHotel') {
      return booking.hotel.imageUrl || HotelPlaceholderImage;
    }
    return HotelPlaceholderImage;
  };

  // Extract pricing information using the utility function
  const { nightlyRate } = getPricingInfo(
    preview
      ? {
          avgPrice: preview.totalBeforeTax / preview.roomCount / booking.nights,
        }
      : booking,
  );

  const subtotal = formatCents(
    preview ? preview.totalBeforeTax / preview.roomCount : booking.basePrice,
  );
  const estimatedTax = formatCents(preview?.totalTax ?? booking.estimatedTax);
  const totalDue = formatCents(
    preview?.totalAfterTax ?? booking.grandTotalAfterTax,
  );

  // Calculate total guests for display in summary section
  const totalGuests = calculateTotalGuests(
    booking.guestsPerRoom,
    booking.rooms,
  );

  return (
    <div>
      <Image
        src={getImageUrl()}
        alt={booking.hotel?.name || 'Hotel'}
        className="aspect-[16/8] w-full rounded-md bg-backgroundGray object-contain"
      />

      {/* Address */}
      <div className="mt-5 border-b border-gray-200 pb-5">
        <h2 className="text-lg font-bold">{booking.hotel?.name}</h2>
        <p className="text-sm">{booking.hotel?.formattedAddress}</p>
      </div>

      {/* Booking Details */}
      <div className="mt-5 space-y-[10px]">
        <h3 className="font-arimo text-sm font-bold text-primaryGray">
          Booking Details
        </h3>
        <div className="flex justify-between text-sm">
          <p>Check-in: {booking.checkInDate}</p>
          <span>→</span>
          <p>Check-out: {booking.checkOutDate}</p>
        </div>
        <div className="flex justify-between text-sm">
          <p>Total length of stay:</p>
          <p>{booking.nights} night(s)</p>
        </div>
        {!hideRoomGuestCounts && (
          <>
            <div className="flex justify-between text-sm">
              <p>Guests:</p>
              <p>{totalGuests} total</p>
            </div>
            <div className="flex justify-between text-sm">
              <p>Rooms:</p>
              <p>{booking.rooms}</p>
            </div>
          </>
        )}
      </div>

      {/* Room Selection */}
      {!hideRoomGuestCounts && (
        <div
          className={`mt-5 space-y-[10px] ${showRatesDetails ? 'border-b border-gray-200 pb-5' : ''}`}
        >
          <h3 className="font-arimo text-sm font-bold text-primaryGray">
            Room Selection
          </h3>
          <p className="text-sm">
            {booking.rooms}x {booking.roomBedType} Bed
          </p>
          <p className="text-sm">
            {booking.roomBedType.toLowerCase()} bed • {booking.guestsPerRoom}{' '}
            guest(s)
          </p>
          {booking.cancellationDetails?.deadline && (
            <p className="text-sm text-accentGreen">
              Cancel until{' '}
              {new Date(booking.cancellationDetails.deadline).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {/* Rates Details - Only show if showRatesDetails is true */}
      {showRatesDetails && (
        <div className="mt-5 space-y-[10px]">
          <h3 className="font-arimo text-sm font-bold text-primaryGray">
            Price Summary
          </h3>

          {/* Per Room Pricing Section */}
          <div className="space-y-3 pt-3">
            <h4 className="text-sm text-gray-500">Per room</h4>
            <div className="flex justify-between text-sm">
              <p>Nightly rate:</p>
              <p>
                {nightlyRate}{' '}
                <span className="text-xs text-gray-500">{PLUS_TAX_TEXT}</span>
              </p>
            </div>
            <div className="flex justify-between text-sm">
              <p>Subtotal:</p>
              <p>
                {subtotal}{' '}
                <span className="text-xs text-gray-500">{PLUS_TAX_TEXT}</span>
              </p>
            </div>
            <div className="text-xs text-gray-500">
              {nightlyRate} × {booking.nights} night
              {booking.nights > 1 ? 's' : ''}
            </div>
          </div>

          {/* Total Pricing Section */}
          <div className="space-y-3 pt-3">
            <h4 className="text-sm text-gray-500">
              Total ({booking.rooms} room{booking.rooms > 1 ? 's' : ''})
            </h4>
            <div className="flex justify-between text-sm">
              <p>Estimated tax:</p>
              <p>{estimatedTax}</p>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-2 text-sm font-bold text-gray-900">
              <p>Total due:</p>
              <p>{totalDue}</p>
            </div>
            <div className="text-xs text-gray-500">
              {preview ? (
                formatCents(preview.totalBeforeTax)
              ) : (
                <>
                  {subtotal} x {booking.rooms} room
                  {booking.rooms > 1 ? 's' : ''}
                </>
              )}{' '}
              + {estimatedTax} tax
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferDetailsCard;
